import {
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router";

// Drop-in shims for the `next/navigation` symbols this codebase uses.
// SPA-only — the Next bundle imports the real package.
//
// Coverage is intentionally narrow: `useRouter().push/replace/back/refresh`,
// `usePathname()`, `useSearchParams()`, `useParams()`. If a caller
// reaches for something else (`redirect`, `notFound`, `permanentRedirect`),
// the throw points at this shim so we know to extend it instead of
// silently doing the wrong thing.

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (href: string) => navigate(href),
    replace: (href: string) => navigate(href, { replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
    refresh: () => {
      // Next's router.refresh() re-runs server components. The SPA has
      // no server components — SWR is responsible for revalidation,
      // and callers that hit refresh() already follow it with their
      // own mutate(). Making this a no-op keeps the call sites happy
      // without lying about behavior.
    },
    prefetch: () => {
      // Same story as <Link prefetch> — we don't have a loader graph
      // yet, so prefetch is a no-op. Next's signature takes a URL
      // string; we drop the arg entirely to silence no-unused-vars.
    },
  };
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useSearchParams(): URLSearchParams {
  // Next returns a read-only URLSearchParams; react-router returns a
  // [params, setter] tuple. Callers across the codebase use the
  // read-only shape, so we surface the first element.
  const [params] = useRouterSearchParams();
  return params;
}

export const useParams = useRouterParams;

export function redirect(url: string): never {
  // Next's redirect() throws a special sentinel that App Router
  // catches; outside of a server component there's no equivalent.
  // The codebase only calls this in src/app/page.tsx, which the SPA
  // replaces with <HomeRedirect />. If something else reaches this,
  // surfacing the throw makes the misuse obvious.
  throw new Error(`redirect(${url}) is not supported in the SPA build`);
}

export function notFound(): never {
  throw new Error("notFound() is not supported in the SPA build");
}
